import {
  ClientEditSVG,
  EmailSvg,
  ResendEmailSvg,
  EyeSVG,
  InputSearchIconSVG,
  PlusIconSVG,
  DropdownButtonChevronUp,
  DropdownButtonChevronDown,
} from "@/assets/svg";
import AppLayout from "@/components/layout/AppLayout";
import Modal from "@/components/Modal";
import NoDataContainer from "@/components/NoDataContainer";
import { useAppDispatch, useAppSelector } from "@/hooks";
import {
  addClient,
  fetchClients,
  getClientDetail,
  resetFilters,
  selectFilteredClients,
  selectNoClients,
  selectPagination,
  selectSearchTerm,
  setSearchTerm,
  updateClient,
  sendClientOnboardEmail,
  resendClientEmail,
  setSort,
  selectSortBy,
  selectSortOrder,
} from "@/store/slices/clientSlice";
import { Client, ClientList, ClientQuery } from "@/types/client.model";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from "@tanstack/react-table";
import debounce from "lodash/debounce";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ClientForm, { ClientFormData } from "./components/ClientForm";
import Tooltip from "@/components/Tooltip";
import SendClientEmailForm from "./components/SendClientEmailForm";
import ResendFailedOnboardEmailForm, { ResendOnboardEmailData } from "./components/ResendFailedOnboardEmailForm";
import { getOnboardEmailViewService, getResendTemplateService } from "@/service/client.service";
import { emailsFromPayload } from "@/utils/emailList";

// Define column meta type for TypeScript
type ColumnMeta = {
  isSticky?: boolean;
  width?: string;
};

// Status badge component
const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  // Capitalize first letter for display
  const displayStatus = status.charAt(0).toUpperCase() + status.slice(1);

  return (
    <span
      className={`px-2 py-1 rounded-sm text-xs font-medium ${status === "active"
        ? "bg-[#EDFFF9] text-[#0C7453]"
        : status === "inactive"
          ? "bg-[#FFEDED] text-[#FF0000]"
          : "bg-[#FFF8ED] text-[#D88C0C]"
        }`}
    >
      {displayStatus}
    </span>
  );
};

// Sortable Header Component
const SortableHeader: React.FC<{
  title: string;
  sortKey: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSort: (key: string) => void;
}> = ({ title, sortKey, sortBy, sortOrder, onSort }) => {
  return (
    <div
      className="flex w-[fit-content] items-center gap-1 cursor-pointer select-none group"
      onClick={() => onSort(sortKey)}
    >
      {title}
      <div className="flex flex-col">
        <DropdownButtonChevronUp
          width={20}
          height={20}
          fill="black"
          className="mb-[-4px]"
        />
        <DropdownButtonChevronDown
          width={20}
          height={20}
          fill="black"
          className="mt-[-4px]"
        />
      </div>
    </div>
  );
};

// Client table component using TanStack React Table
const ClientTable: React.FC<{
  data: ClientList[];
  onEdit: (clientId: string) => void;
  onView: (clientId: string) => void;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    pageSize: number;
  };
  onPageChange: (page: number) => void;
  onViewEmail: (
    emailId: string,
    status: string,
    clientName: string,
    orgName: string,
    clientId: string
  ) => void;
  onResendEmail: (
    userId: string,
    clientName: string,
    orgName: string,
    email: string,
    listCc?: string[],
    listBcc?: string[]
  ) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSort: (key: string) => void;
}> = ({ data, onEdit, onView, pagination, onPageChange, onViewEmail, onResendEmail, sortBy, sortOrder, onSort }) => {
  const columnHelper = createColumnHelper<ClientList>();

  const columns = useMemo(
    () => [
      columnHelper.accessor("org_name", {
        header: () => (
          <SortableHeader
            title="Organization name"
            sortKey="org_name"
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={onSort}
          />
        ),
        cell: (info) => (
          <div
            title={info.getValue()}
            style={{
              overflowWrap: "anywhere",
              whiteSpace: "pre-wrap",
            }}
          >
            {info.getValue()}
          </div>
        ),
        meta: {
          width: "234px",
          maxWidth: "234px",
        } as ColumnMeta,
      }),
      columnHelper.accessor(
        (row) => `${row.user.first_name} ${row.user.last_name}`,
        {
          id: "name",
          header: () => (
            <SortableHeader
              title="Client name"
              sortKey="name"
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={onSort}
            />
          ),
          cell: (info) => (
            <div
              title={info.getValue()}
              style={{ overflowWrap: "anywhere", whiteSpace: "pre-wrap" }}
            >
              {info.getValue()}
            </div>
          ),
          meta: {
            width: "250px",
            maxWidth: "250px",
          } as ColumnMeta,
        }
      ),
      columnHelper.accessor((row) => row.user.email, {
        header: "Email address",
        cell: (info) => (
          <div
            title={info.getValue()}
            style={{
              overflowWrap: "anywhere",
              whiteSpace: "pre-wrap",
            }}
          >
            {info.getValue()}
          </div>
        ),
        meta: {
          width: "300px",
          maxWidth: "300px",
        } as ColumnMeta,
      }),
      columnHelper.accessor((row) => row.user.contact_number, {
        header: "Phone",
        cell: (info) => info.getValue(),
        meta: {
          width: "160px",
          maxWidth: "160px",
        } as ColumnMeta,
      }),
      columnHelper.accessor("location", {
        id: "location",
        header: "Location",
        cell: (info) => (
          <div className="text-wrap">
            {info.row.original.location.city || ""}{" "}
            {info.row.original.location?.city &&
              info.row.original.location?.state
              ? ", "
              : ""}{" "}
            {info.row.original.location?.state || ""}
          </div>
        ),
        meta: {
          width: "206px",
          maxWidth: "206px",
        } as ColumnMeta,
      }),
      columnHelper.accessor((row) => row.user.status, {
        header: "Status",
        cell: (info) => <StatusBadge status={info.getValue() as string} />,
        meta: {
          width: "120px",
          maxWidth: "120px",
        } as ColumnMeta,
      }),
      columnHelper.display({
        id: "actions",
        header: "Action",
        cell: (info) => {
          const showEmailButton = info.row.original.onboard_email_details;
          const emailSvgColor = showEmailButton
            ? showEmailButton.status === "sent"
              ? "#059669"
              : showEmailButton.status === "pending"
                ? "#F59E0B"
                : "#DC2626"
            : "#DC2626";
          const clientName =
            `${info.row.original.user.first_name} ${info.row.original.user.last_name}`.trim();
          const orgName = info.row.original.org_name;
          return (
            <div className="flex gap-2 items-center justify-start">
              <Tooltip content="View">
                <button
                  onClick={() => onView(info.row.original.id as string)}
                  className="cursor-pointer"
                >
                  <EyeSVG />
                </button>
              </Tooltip>
              <Tooltip content="Edit">
                <button
                  onClick={() => onEdit(info.row.original.id as string)}
                  className="cursor-pointer"
                >
                  <ClientEditSVG />
                </button>
              </Tooltip>
              {showEmailButton && (
                <Tooltip content="Send email">
                  <button
                    onClick={() =>
                      onViewEmail(
                        showEmailButton.id,
                        showEmailButton.status,
                        clientName,
                        orgName,
                        info.row.original.user?.id as string
                      )
                    }
                    className="cursor-pointer"
                  >
                    <EmailSvg color={emailSvgColor} />
                  </button>
                </Tooltip>
              )}
              {showEmailButton?.is_resend_available && (
                <Tooltip content="Resend email">
                  <button
                    onClick={() =>
                      onResendEmail(
                        info.row.original.user?.id as string,
                        clientName,
                        orgName,
                        info.row.original.user?.email as string,
                        info.row.original.cc,
                        info.row.original.bcc
                      )
                    }
                    className="cursor-pointer"
                  >
                    <ResendEmailSvg color="#73B5C5" />
                  </button>
                </Tooltip>
              )}
            </div>
          );
        },
        meta: {
          isSticky: true,
          width: "140px",
        } as ColumnMeta,
      }),
    ],
    [columnHelper, onEdit, onView, onResendEmail, sortBy, sortOrder, onSort]
  );

  // Use external pagination from API instead of client-side pagination
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    // Don't use getPaginationRowModel as we're using server-side pagination
    manualPagination: true,
    pageCount: pagination.totalPages,
  });



  return (
    <div className="flex flex-col">
      <div className="overflow-x-auto relative py-4">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-[#F4F7F9] text-left">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const isSticky = (header.column.columnDef.meta as any)
                    ?.isSticky;
                  return (
                    <th
                      key={header.id}
                      scope="col"
                      style={{
                        width: (header.column.columnDef.meta as ColumnMeta)
                          ?.width,
                        minWidth: (header.column.columnDef.meta as ColumnMeta)
                          ?.width,
                        maxWidth: (header.column.columnDef.meta as ColumnMeta)
                          ?.width,
                      }}
                      className={`p-3 text-sm font-[600] text-[#1E1E1E] ${isSticky
                        ? "sticky right-0 bg-[#F4F7F9] z-10 shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]"
                        : ""
                        }`}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody className="bg-white divide-y divide-gray-200 border-l-1 border-r-1 border-b-1 border-gray-200">
            {data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="text-center py-4">
                  No clients found
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="hover:bg-[#F9FEFF]">
                  {row.getVisibleCells().map((cell) => {
                    const isSticky = (cell.column.columnDef.meta as ColumnMeta)
                      ?.isSticky;
                    return (
                      <td
                        key={cell.id}
                        style={{
                          width: (cell.column.columnDef.meta as ColumnMeta)
                            ?.width,
                          minWidth: (cell.column.columnDef.meta as ColumnMeta)
                            ?.width,
                          maxWidth: (cell.column.columnDef.meta as ColumnMeta)
                            ?.width,
                        }}
                        className={`whitespace-nowrap text-sm text-[#1E1E1E] font-[500] ${isSticky
                          ? "sticky right-[-1px] my-0.5 z-10"
                          : "px-3 py-4"
                          }`}
                      >
                        {isSticky ? (
                          <div className="p-3 bg-white">
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </div>
                        ) : (
                          flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-4 text-sm text-[#1E1E1E] font-[600] flex justify-between items-center">
        <div>
          Showing{" "}
          {pagination.currentPage === 1
            ? 1
            : (pagination.currentPage - 1) * pagination.pageSize + 1}{" "}
          to{" "}
          {Math.min(
            pagination.currentPage * pagination.pageSize,
            pagination.totalItems
          )}{" "}
          of {pagination.totalItems} results
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={() => {
              if (pagination.currentPage > 1) {
                onPageChange(pagination.currentPage - 1);
              }
            }}
            disabled={pagination.currentPage <= 1}
            className="px-3 py-1 w-[78px] h-[34px] text-sm text-[#1E1E1E] bg-[#F5F5F5] rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            Previous
          </button>
          <button
            onClick={() => {
              if (pagination.currentPage < pagination.totalPages) {
                onPageChange(pagination.currentPage + 1);
              }
            }}
            disabled={pagination.currentPage >= pagination.totalPages}
            className="px-3 py-1 w-[78px] h-[34px] text-sm text-white bg-[#73B5C5] rounded-md hover:bg-[#5da7b8] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

const Clients: React.FC = () => {
  const dispatch = useAppDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [modalMode, setModalMode] = useState<"add" | "edit" | "view">("add");
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isResendEmailModalOpen, setIsResendEmailModalOpen] = useState(false);
  const [resendEmailMeta, setResendEmailMeta] = useState<{
    userId: string;
    clientName: string;
    orgName: string;
    to: string;
    cc: string;
    bcc: string;
    subject: string;
    content: string;
  }>({ userId: "", clientName: "", orgName: "", to: "", cc: "", bcc: "", subject: "", content: "" });
  const [isResendFailedOpen, setIsResendFailedOpen] = useState(false);
  const [resendFailedData, setResendFailedData] = useState<ResendOnboardEmailData | null>(null);
  const [emailMeta, setEmailMeta] = useState<{
    clientId: string;
    clientName: string;
    orgName: string;
    to: string;
    cc?: string;
    bcc?: string;
    subject?: string;
    content?: string;
    sentDate?: string;
    sentTime?: string;
    viewMode?: boolean;
    emailStatus?: string;
  }>({
    clientId: "",
    clientName: "",
    orgName: "",
    to: "",
    cc: "",
    bcc: "",
    subject: "",
    content: "",
    sentDate: "",
    sentTime: "",
    viewMode: false,
    emailStatus: undefined,
  });

  // Get client data from Redux store
  const filteredClients = useAppSelector(selectFilteredClients);
  const searchTerm = useAppSelector(selectSearchTerm);
  const pagination = useAppSelector(selectPagination);
  const noClients = useAppSelector(selectNoClients);
  const sortBy = useAppSelector(selectSortBy);
  const sortOrder = useAppSelector(selectSortOrder);

  // Use ref to track if initial data has been loaded
  const initialDataLoaded = useRef(false);

  // Create a debounced search function
  const debouncedSearch = useCallback(
    debounce((searchValue: string) => {
      dispatch(
        fetchClients({
          page: 1, // Reset to first page when searching
          pageSize: pagination.pageSize,
          search: searchValue || undefined,
        })
      );
    }, 500),
    [dispatch, pagination.pageSize]
  );

  // Handle search term changes
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    dispatch(setSearchTerm(value));
    debouncedSearch(value);
  };

  const handleSort = (key: string) => {
    let newSortOrder: "asc" | "desc" = "asc";
    if (sortBy === key && sortOrder === "asc") {
      newSortOrder = "desc";
    } else if (sortBy === key && sortOrder === "desc") {
      newSortOrder = "asc";
    }

    dispatch(setSort({ sortBy: key, sortOrder: newSortOrder }));
    dispatch(
      fetchClients({
        page: 1, // Reset to first page on sort
        pageSize: pagination.pageSize,
        search: searchTerm || undefined,
        sortBy: key, // We can pass these explicitely or let the thunk pick them up from state. 
        // Note: dispatch(setSort) is sync, but the state update might strictly happen after. 
        // Actually, passing them explicitly is safer if we want to ensure they are used in this call.
        // However, since we updated the thunk to read from state, and state update via dispatch IS synchronous in Redux Toolkit (unless using async middleware), 
        // it should pick up the new values if we just call fetchClients(). 
        // BUT, `fetchClients` reads `state.clients` which is the updated state.
        // Let's pass them explicitely to be safe and clear.
        sortOrder: newSortOrder,
      })
    );
  };

  const handleOpenModal = () => {
    setModalMode("add");
    setEditingClient(null);
    setIsModalOpen(true);
  };

  const handleEditClient = (clientId: string) => {
    setModalMode("edit");

    // Fetch the latest client details from the API instead of using table data
    dispatch(getClientDetail(clientId))
      .unwrap()
      .then((clientData) => {
        setEditingClient(clientData as Client);
        setIsModalOpen(true);
      })
      .catch((error: Error) => {
        console.error("Failed to fetch client details:", error);
      });
  };

  const handleViewClient = (clientId: string) => {
    setModalMode("view");

    // Fetch the latest client details from the API instead of using table data
    dispatch(getClientDetail(clientId))
      .unwrap()
      .then((clientData) => {
        setEditingClient(clientData as Client);
        setIsModalOpen(true);
      })
      .catch((error: Error) => {
        console.error("Failed to fetch client details:", error);
      });
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingClient(null);
  };

  const onViewEmail = async (
    emailId: string,
    status: string,
    clientName: string,
    orgName: string,
    clientId: string
  ) => {
    try {
      const { data } = await getOnboardEmailViewService(emailId);
      const payload = data?.data || {};
      const normStatus = (status || "").toLowerCase();
      // Derive fields defensively
      const toArr: string[] = payload.to || [];
      const ccArr: string[] = payload.cc || [];
      const bccArr: string[] = payload.bcc || [];
      const subject: string = payload.subject || "";
      const body: string = payload.body || payload.html || "";
      const sentAt: string | undefined = payload.sent_at || payload.sentAt;
      let sentDate = "";
      let sentTime = "";
      if (sentAt) {
        const d = new Date(sentAt);
        if (!isNaN(d.getTime())) {
          sentDate = d.toLocaleDateString();
          sentTime = d.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          });
        }
      } else {
        sentDate = payload.send_date || "";
        sentTime = payload.send_time || "";
      }
      if (normStatus === "failed") {
        const reason: string | null = payload.error || payload.reason || null;
        const role: string | null = payload?.owner_details?.client?.user?.role
          || payload.user_role || null;
        setResendFailedData({
          reason,
          clientName,
          role,
          to: toArr,
          cc: ccArr,
          bcc: bccArr,
          subject,
          bodyHtml: body,
        });
        setEmailMeta((prev) => ({ ...prev, clientId }));
        setIsResendFailedOpen(true);
      } else {
        setEmailMeta((prev) => ({
          ...prev,
          clientId,
          clientName,
          orgName,
          to: toArr.join(", "),
          cc: ccArr.join(", "),
          bcc: bccArr.join(", "),
          subject,
          content: body,
          sentDate,
          sentTime,
          viewMode: normStatus === "sent",
          emailStatus: normStatus,
        }));
        setIsEmailModalOpen(true);
      }
    } catch (e) {
      console.error("Failed to fetch onboarding email details", e);
    }
  };

  type EmailFormPayload = {
    to: string[];
    cc: string[];
    bcc: string[];
    subject: string;
    content: string;
  };

  const handleSendLater = (p: EmailFormPayload) => {
    if (!emailMeta.clientId) return;
    dispatch(
      // @ts-ignore thunk
      sendClientOnboardEmail({
        clientId: emailMeta.clientId,
        payload: {
          action: "send_later",
          to: p.to,
          cc: p.cc,
          bcc: p.bcc,
          subject: p.subject,
          body: p.content,
        },
      })
    )
      // @ts-ignore unwrap
      .unwrap()
      .then(() => {
        setIsEmailModalOpen(false);
        dispatch(
          fetchClients({
            page: pagination.currentPage,
            pageSize: pagination.pageSize,
            search: searchTerm || undefined,
          })
        );
      });
  };

  const handleSendNow = (p: EmailFormPayload) => {
    if (!emailMeta.clientId) return;
    dispatch(
      // @ts-ignore thunk
      sendClientOnboardEmail({
        clientId: emailMeta.clientId,
        payload: {
          action: "send_now",
          to: p.to,
          cc: p.cc,
          bcc: p.bcc,
          subject: p.subject,
          body: p.content,
        },
      })
    )
      // @ts-ignore unwrap
      .unwrap()
      .then(() => {
        setIsEmailModalOpen(false);
        dispatch(
          fetchClients({
            page: pagination.currentPage,
            pageSize: pagination.pageSize,
            search: searchTerm || undefined,
          })
        );
      });
  };

  const onResendEmail = async (
    userId: string,
    clientName: string,
    orgName: string,
    email: string,
    listCc?: string[],
    listBcc?: string[]
  ) => {
    try {
      const { data } = await getResendTemplateService(userId);
      const payload = data?.data || {};
      const tplCc = emailsFromPayload(
        payload.cc ?? (payload as { CC?: unknown }).CC
      );
      const tplBcc = emailsFromPayload(
        payload.bcc ?? (payload as { BCC?: unknown }).BCC
      );
      const ccArr =
        listCc && listCc.length > 0 ? listCc : tplCc;
      const bccArr =
        listBcc && listBcc.length > 0 ? listBcc : tplBcc;
      const subject: string = payload.subject || "";
      const body: string = payload.body || payload.html || "";

      setResendEmailMeta({
        userId,
        clientName,
        orgName,
        to: email,
        cc: ccArr.join(", "),
        bcc: bccArr.join(", "),
        subject,
        content: body,
      });
      setIsResendEmailModalOpen(true);
    } catch (e) {
      console.error("Failed to fetch resend template", e);
    }
  };

  const handleResendSendNow = (p: EmailFormPayload) => {
    if (!resendEmailMeta.userId) return;
    dispatch(
      // @ts-ignore thunk
      resendClientEmail({
        userId: resendEmailMeta.userId,
        payload: {
          action: "send_now",
          to: p.to,
          cc: p.cc,
          bcc: p.bcc,
          subject: p.subject,
          body: p.content,
        },
      })
    )
      // @ts-ignore unwrap
      .unwrap()
      .then(() => {
        setIsResendEmailModalOpen(false);
        dispatch(
          fetchClients({
            page: pagination.currentPage,
            pageSize: pagination.pageSize,
            search: searchTerm || undefined,
          })
        );
      });
  };

  // Fetch clients on component mount (only once)
  useEffect(() => {
    if (!initialDataLoaded.current) {
      const initialQuery: ClientQuery = {
        page: 1,
        pageSize: 100,
      };
      dispatch(fetchClients(initialQuery));
      initialDataLoaded.current = true;
    }

    // Reset filters when component unmounts
    return () => {
      dispatch(resetFilters());
    };
  }, [dispatch]);

  const handleAddClient = (clientData: ClientFormData, meta?: { emailChanged?: boolean }) => {
    // Ensure status is properly typed
    const clientStatus =
      (clientData.status as "active" | "inactive" | "pending") || "active";

    if (modalMode === "edit" && editingClient) {
      // Update existing client
      const updatedClient: Client = {
        ...editingClient,
        first_name: clientData.first_name,
        last_name: clientData.last_name,
        org_name: clientData.org_name,
        email: clientData.email,
        cc: clientData.cc || [],
        bcc: clientData.bcc || [],
        contact_number: clientData.contact_number,
        website: clientData.website,
        address: clientData.address,
        city: clientData.city,
        state: clientData.state,
        country: clientData.country,
        pincode: clientData.pincode,
        location: clientData.location,
        status: clientStatus,
      };

      // Dispatch update client action and close modal only after success
      dispatch(updateClient(updatedClient))
        .unwrap()
        .then((updated) => {
          setIsModalOpen(false);
          setEditingClient(null);
          // Refresh the client list with current filters
          dispatch(
            fetchClients({
              page: pagination.currentPage,
              pageSize: pagination.pageSize,
              search: searchTerm || undefined,
            })
          );

          if (meta?.emailChanged) {
            const u = updated as Client;
            const ccList = u.cc?.length ? u.cc : clientData.cc || [];
            const bccList = u.bcc?.length ? u.bcc : clientData.bcc || [];
            setEmailMeta((prev) => ({
              ...prev,
              clientId: u.id,
              clientName: `${u.first_name} ${u.last_name}`,
              orgName: u.org_name,
              to: u.email,
              cc: ccList.join(", "),
              bcc: bccList.join(", "),
              subject: u.email_prefill?.subject,
              content: u.email_prefill?.body,
              viewMode: false,
              emailStatus: undefined,
            }));
            setIsEmailModalOpen(true);
          }
        })
        .catch((error) => {
          console.error("Failed to update client:", error);
          // Modal stays open on error
        });
    } else {
      // Create a new client object
      const newClient: Client = {
        id: "", // ID will be assigned by the server
        first_name: clientData.first_name,
        last_name: clientData.last_name,
        org_name: clientData.org_name,
        email: clientData.email,
        cc: clientData.cc || [],
        bcc: clientData.bcc || [],
        contact_number: clientData.contact_number,
        website: clientData.website,
        address: clientData.address,
        city: clientData.city,
        state: clientData.state,
        country: clientData.country,
        pincode: clientData.pincode,
        location: clientData.location,
        status: "active",
      };

      // Dispatch the addClient action and close modal only after success
      dispatch(addClient(newClient))
        .unwrap()
        .then((created) => {
          setIsModalOpen(false);
          setEditingClient(null);
          setEmailMeta({
            clientId: (created as Client).id,
            clientName: `${clientData.first_name} ${clientData.last_name}`,
            orgName: clientData.org_name,
            to: clientData.email,
            cc: (clientData.cc || []).join(", "),
            bcc: (clientData.bcc || []).join(", "),
            subject: created?.email_prefill?.subject,
            content: created?.email_prefill?.body,
          });
          setIsEmailModalOpen(true);
        })
        .catch((error) => {
          console.error("Failed to add client:", error);
          // Modal stays open on error
        });
    }
  };

  return (
    <AppLayout
      title="Clients"
      contentClassName={`min-h-[90%] ${pagination.totalPages ? "" : "flex items-center justify-center"
        }`}
      rightActions={
        <button
          onClick={handleOpenModal}
          className="bg-[#73B5C5] hover:bg-[#5da7b8] text-white px-4 py-2 rounded-md text-sm flex items-center cursor-pointer gap-1"
        >
          Add new client
          <PlusIconSVG />
        </button>
      }
    >
      <div className="p-4">
        {noClients ? (
          <NoDataContainer
            title="No clients added yet!"
            description="Start by adding your clients to manage their projects"
            buttonLabel="Add client"
            onButtonClick={handleOpenModal}
          />
        ) : (
          <div className="bg-white rounded-lg">
            {/* Search */}
            <div className="px-0 py-4 flex flex-wrap items-center justify-between gap-2">
              <div className="relative flex-grow max-w-[246px]">
                <input
                  type="text"
                  placeholder="Search"
                  className="w-full h-[42px] px-3 pl-9 bg-[#F9FBFF] rounded-[6px] focus:outline-none focus:border-[#73B5C5] placeholder:text-[#B5B7C0]"
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
                <div className="absolute inset-y-0 left-0 flex items-center pl-2.5 pointer-events-none">
                  <InputSearchIconSVG />
                </div>
              </div>
            </div>

            {/* Table with TanStack React Table */}
            <ClientTable
              data={filteredClients}
              onEdit={handleEditClient}
              onView={handleViewClient}
              pagination={pagination}
              onViewEmail={onViewEmail}
              onResendEmail={onResendEmail}
              onPageChange={(page) => {
                dispatch(
                  fetchClients({
                    page,
                    pageSize: pagination.pageSize,
                    search: searchTerm || undefined,
                  })
                );
              }}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={handleSort}
            />
          </div>
        )}
      </div>

      {/* Modal for adding/editing clients */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={
          modalMode === "add"
            ? "Add client"
            : modalMode === "edit"
              ? "Edit client"
              : "View client"
        }
        width="min(95%, 868px)"
        contentClassName={modalMode === "view" ? "pt-0" : "p-0"}
      >
        <ClientForm
          onSubmit={handleAddClient}
          initialData={editingClient || undefined}
          onCancel={handleCloseModal}
          viewMode={modalMode === "view"}
        />
      </Modal>

      <Modal
        isOpen={isEmailModalOpen}
        onClose={() => setIsEmailModalOpen(false)}
        title={emailMeta.viewMode ? "View email" : "Send email"}
        width="min(95%, 868px)"
        contentClassName="p-0 max-h-[calc(100vh-100px)] overflow-y-auto"
      >
        <SendClientEmailForm
          clientName={emailMeta.clientName}
          orgName={emailMeta.orgName}
          initialTo={emailMeta.to}
          initialCc={emailMeta.cc}
          initialBcc={emailMeta.bcc}
          initialSubject={emailMeta.subject}
          initialContent={emailMeta.content}
          sentDate={emailMeta.sentDate}
          sentTime={emailMeta.sentTime}
          viewMode={emailMeta.viewMode}
          emailStatus={emailMeta.emailStatus}
          onCancel={() => setIsEmailModalOpen(false)}
          onSendLater={handleSendLater}
          onSendNow={handleSendNow}
        />
      </Modal>

      <Modal
        isOpen={isResendEmailModalOpen}
        onClose={() => setIsResendEmailModalOpen(false)}
        title="Resend email"
        width="min(95%, 868px)"
        contentClassName="p-0 max-h-[calc(100vh-100px)] overflow-y-auto"
      >
        <SendClientEmailForm
          clientName={resendEmailMeta.clientName}
          orgName={resendEmailMeta.orgName}
          initialTo={resendEmailMeta.to}
          initialCc={resendEmailMeta.cc}
          initialBcc={resendEmailMeta.bcc}
          initialSubject={resendEmailMeta.subject}
          initialContent={resendEmailMeta.content}
          onCancel={() => setIsResendEmailModalOpen(false)}
          onSendLater={() => setIsResendEmailModalOpen(false)}
          onSendNow={handleResendSendNow}
          sendLaterLabel="Cancel"
        />
      </Modal>

      <Modal
        isOpen={isResendFailedOpen}
        onClose={() => setIsResendFailedOpen(false)}
        title="Email failed to send"
        width="min(95%, 868px)"
        contentClassName="p-0 max-h-[calc(100vh-100px)] overflow-y-auto"
      >
        {resendFailedData ? (
          <ResendFailedOnboardEmailForm
            data={resendFailedData}
            onCancel={() => setIsResendFailedOpen(false)}
            onSubmit={async ({ to, cc, bcc, subject, body }) => {
              if (!emailMeta.clientId) return;
              await dispatch(
                // @ts-ignore thunk
                sendClientOnboardEmail({
                  clientId: emailMeta.clientId,
                  payload: { action: "send_now", to, cc, bcc, subject, body },
                })
              )
                // @ts-ignore unwrap
                .unwrap()
                .then(() => {
                  setIsResendFailedOpen(false);
                  dispatch(
                    fetchClients({
                      page: pagination.currentPage,
                      pageSize: pagination.pageSize,
                      search: searchTerm || undefined,
                    })
                  );
                });
            }}
          />
        ) : null}
      </Modal>
    </AppLayout>
  );
};

export default Clients;
